import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, BriefcaseIcon, CalendarIcon, CheckIcon, ClockIcon, GraduationCapIcon, LayersIcon, MapPinIcon, MonitorIcon, TrendingUpIcon, UserIcon } from 'lucide-react';
import { getCareerRoleDetail, getDomain, getProgramHeroImage, getProgramMatch, getProgramsByDomain, programsIndexSlug, type ProgramTemplate } from '../data/programTemplates';
import { buildMatchLink } from '../utils/matchParams';
import { HeroMatchForm } from '../components/HeroMatchForm';
import { LegalFooter } from '../components/LegalFooter';
import { usePageMeta } from '../hooks/usePageMeta';
interface ProgramTemplatePageProps {
  program: ProgramTemplate;
}
export function ProgramTemplatePage({
  program
}: ProgramTemplatePageProps) {
  const domain = getDomain(program.domainSlug);
  const siblings = getProgramsByDomain(program.domainSlug);
  const related = siblings.filter(p => p.slug !== program.slug);
  const heroImage = getProgramHeroImage(program.slug);
  usePageMeta(program.metaTitle, program.metaDescription, {
    ogType: 'article'
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [program.slug]);

  // Fallback path for visitors who scroll past the hero form.
  const matchHref = buildMatchLink('/request-information', getProgramMatch(program));
  const scrollToForm = () => {
    document.getElementById('hero-match-form')?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  };
  const stats = [{
    icon: CalendarIcon,
    label: 'Program Length',
    value: `${program.weeks} weeks`
  }, {
    icon: LayersIcon,
    label: 'Credits',
    value: `${program.credits} credits`
  }, {
    icon: ClockIcon,
    label: 'Instructional Hours',
    value: program.instructionalHours.toLocaleString()
  }, {
    icon: MonitorIcon,
    label: 'Format',
    value: program.format
  }];
  return <main className="min-h-screen bg-white" data-id="element-2271" data-mp="34tw6opke6vkr">
      {/* Hero. The photograph shows the work this program leads to; the navy
          base and scrim keep the headline legible while it loads and at widths
          where the image doesn't fully cover. */}
      <section className="relative bg-navy overflow-hidden" data-id="element-2272" data-mp="1dqcms8qipvz2">
        {heroImage && <>
            {/* Mirrored so the photo's subject sits on the left, in the open
                band beside the headline, instead of behind the form card. */}
            <img src={heroImage} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover object-center lg:scale-x-[-1]" data-id="element-2273" data-mp="1rpvig2257euh" />
            <div className="absolute inset-0 bg-navy/80" aria-hidden="true" data-id="element-2274" data-mp="1wn43caeodwzf" />
          </>}
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-7 lg:py-20" data-id="element-2275" data-mp="21gq7tfaoquk5">
          <nav aria-label="Breadcrumb" className="hidden lg:block mb-6" data-id="element-2276" data-mp="26dgl5uv3vc1v">
            <ol className="flex flex-wrap items-center gap-2 text-xs text-white/70" data-id="element-2277" data-mp="2agdae4f5i6es">
              <li data-id="element-2278" data-mp="2ff6e554myj1r">
                <Link to={`/program-information/${programsIndexSlug}`} className="hover:text-white transition-colors duration-200" data-id="element-2279" data-mp="2fen74jgixf2i">
                  New Online Programs
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-2280" data-mp="3uxurh9cmtv4o">/</li>
              <li className="text-white/90" data-id="element-2281" data-mp="3v08nplj1tddt">{domain?.name}</li>
            </ol>
          </nav>

          {/* Explicit grid placement rather than a duplicated form: on mobile
              the form follows the headline so it lands within reach, and the
              supporting trust points move below it. */}
          <div className="grid gap-y-5 lg:grid-cols-5 lg:gap-y-7 lg:gap-x-14 items-start" data-id="element-2282" data-mp="mk5f9dk43kof">
            <div className="lg:col-span-3 lg:col-start-1 lg:row-start-1" data-id="element-2283" data-mp="iitwnrznnzpq">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-[11px] font-semibold uppercase tracking-widest text-gold mb-3 lg:mb-5" data-id="element-2284" data-mp="dofyjhtlzehd">
                <GraduationCapIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-2285" data-mp="1673c8c69zr8f" />
                {program.credential}
              </span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-3 lg:mb-5" data-id="element-2286" data-mp="11dqpy8p2jwiu">
                {program.heroHeadline}
              </h1>
              <p className="text-base lg:text-lg text-white/80 leading-relaxed max-w-2xl" data-id="element-2287" data-mp="q0qdxlnmqm7j">
                {program.heroSubhead}
              </p>
            </div>

            <div id="hero-match-form" className="lg:col-span-2 lg:col-start-4 lg:row-start-1" data-id="element-2288" data-mp="gxpc3x5srh9o">
              <HeroMatchForm programs={siblings.length > 0 ? siblings : [program]} initialProgramSlug={program.slug} data-id="element-2289" data-mp="14ntddxvij4xi" />
            </div>

            <ul className="lg:col-span-3 lg:col-start-1 lg:row-start-2 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/80" style={{
            gap: "10px",
            alignItems: "center",
            justifyContent: "center"
          }} data-id="element-2290" data-mp="18p53e4iats68">
              <li className="flex items-center gap-2" data-id="element-2291" data-mp="zvs2afa2zk92">
                <CheckIcon className="w-4 h-4 text-gold flex-shrink-0" aria-hidden="true" data-id="element-2292" data-mp="1nme0zfy73s6e" />
                {program.format}
              </li>
              <li className="flex items-center gap-2" data-id="element-2293" data-mp="1seysubxjjv7a">
                <CheckIcon className="w-4 h-4 text-gold flex-shrink-0" aria-hidden="true" data-id="element-2294" data-mp="21rxt6luikn6o" />
                {program.weeks} weeks
              </li>
              <li className="flex items-center gap-2" data-id="element-2295" data-mp="1xqm54kraga0l">
                <CheckIcon className="w-4 h-4 text-gold flex-shrink-0" aria-hidden="true" data-id="element-2296" data-mp="2biir072d5noj" />
                Free to get matched
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Stat row */}
      <section className="border-b border-gray-200 bg-light" data-id="element-2297" data-mp="1iu3pmnc5n3vo">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-2298" data-mp="1esi46dlk1h9v">
          <dl className="grid grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2299" data-mp="1eucp3rwtt9dy">
            {stats.map(stat => <div key={stat.label} className="flex items-start gap-3" style={{
            alignItems: "center",
            justifyContent: "center"
          }} data-id="element-2300" data-mp="1sponc9a6xhzm">
                <stat.icon className="w-5 h-5 mt-0.5 flex-shrink-0 text-crimson" aria-hidden="true" data-id="element-2301" data-mp="346og160g9yu6" />
                <div data-id="element-2302" data-mp="2z8d3gnirta89">
                  <dt className="text-[11px] font-semibold uppercase tracking-wider text-gray-500" data-id="element-2303" data-mp="2z6sa3186zxbz">
                    {stat.label}
                  </dt>
                  <dd className="text-sm font-bold text-gray-900 mt-0.5" data-id="element-2304" data-mp="3ba4o1h8qsk7p">
                    {stat.value}
                  </dd>
                </div>
              </div>)}
          </dl>
        </div>
      </section>

      {/* Body */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-id="element-2305" data-mp="3l8khhqv8s5kd">
        <div className="grid lg:grid-cols-3 gap-12" data-id="element-2306" data-mp="2nkk5gzgi3oyy">
          <div className="lg:col-span-2 space-y-12" data-id="element-2307" data-mp="2nm4ygkn62zup">
            <section aria-labelledby="overview-heading" data-id="element-2308" data-mp="2sjdfuj0hqzuw">
              <h2 id="overview-heading" className="text-2xl font-bold text-navy mb-5" data-id="element-2309" data-mp="2sjwnn6wfg7v7">
                Program Overview
              </h2>
              <div className="space-y-4" data-id="element-2310" data-mp="3296iuvmki6nj">
                {program.overview.map((paragraph, idx) => <p key={idx} className="text-gray-700 leading-relaxed" data-id="element-2311" data-mp="24u2dix3hsxen" data-mpi={idx}>
                    {paragraph}
                  </p>)}
              </div>
            </section>

            {program.concentrations && <section aria-labelledby="concentrations-heading" data-id="element-2312" data-mp="29qsv5ouy4126">
                <h2 id="concentrations-heading" className="text-2xl font-bold text-navy mb-5" data-id="element-2313" data-mp="2294edfp71b8c">
                  Program Concentrations
                </h2>
                <div className="grid sm:grid-cols-2 gap-4" data-id="element-2314" data-mp="1sax9vwc3cln8">
                  {program.concentrations.map(c => <div key={c.name} className="border border-gray-200 rounded-xl p-5 flex flex-col" data-id="element-2315" data-mp="1ncly3g28k12d">
                      <h3 className="font-bold text-navy mb-2" data-id="element-2316" data-mp="1jba8hajd3fu6">{c.name}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed" data-id="element-2317" data-mp="1ek1yaca7jqfy">
                        {c.description}
                      </p>
                    </div>)}
                </div>
              </section>}

            <section aria-labelledby="features-heading" data-id="element-2318" data-mp="14m4afpw9fvnv">
              <h2 id="features-heading" className="text-2xl font-bold text-navy mb-5" data-id="element-2319" data-mp="14np5rhqefims">
                Unique Program Features
              </h2>
              <ul className="space-y-3" data-id="element-2320" data-mp="vlftambklf6r">
                {program.features.map(feature => <li key={feature} className="flex items-start" data-id="element-2321" data-mp="vnjzjawxyqa7">
                    <CheckIcon className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5 text-crimson" aria-hidden="true" data-id="element-2322" data-mp="2jif2y3bd21tu" />
                    <span className="text-gray-700" data-id="element-2323" data-mp="2og73tmtzgya6">{feature}</span>
                  </li>)}
              </ul>
            </section>

            {program.studentProfile && <section aria-labelledby="profile-heading" data-id="element-2324" data-mp="2ehzrjhan34ei">
                <h2 id="profile-heading" className="text-2xl font-bold text-navy mb-4" data-id="element-2325" data-mp="2eklwraa257tk">
                  Who This Program Is For
                </h2>
                <div className="border-l-4 border-crimson pl-5" data-id="element-2326" data-mp="25j5wvwgvco3l">
                  <p className="text-gray-700 leading-relaxed" data-id="element-2327" data-mp="1qtgi2yfuslku">
                    {program.studentProfile}
                  </p>
                </div>
              </section>}

          </div>

          {/* Sidebar. Sticky so it travels with the reader instead of leaving a
              tall empty column beside the long-form content. */}
          <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start" data-id="element-2328" data-mp="1lstuthunpu8g">
            <div className="border border-gray-200 rounded-2xl p-6 bg-light" data-id="element-2329" data-mp="135ba4ueckgcv">
              <h2 className="text-sm font-bold uppercase tracking-wider text-gray-500 mb-4" data-id="element-2330" data-mp="y6qaaw30byoy">
                At a Glance
              </h2>
              <dl className="space-y-3 text-sm" data-id="element-2331" data-mp="1c7kgd82l1nkg">
                <div className="flex justify-between gap-4" data-id="element-2332" data-mp="1c454sf84awns">
                  <dt className="text-gray-500" data-id="element-2333" data-mp="175v63un6pdxf">Credential</dt>
                  <dd className="font-semibold text-gray-900 text-right" data-id="element-2334" data-mp="1m45mxxu936qe">
                    {program.credential}
                  </dd>
                </div>
                <div className="flex justify-between gap-4" data-id="element-2335" data-mp="1h3hynjxep3zv">
                  <dt className="text-gray-500" data-id="element-2336" data-mp="1uvxy6ar3mjw1">Length</dt>
                  <dd className="font-semibold text-gray-900 text-right" data-id="element-2337" data-mp="1qve1t9ecnkay">
                    {program.weeks} weeks
                  </dd>
                </div>
                <div className="flex justify-between gap-4" data-id="element-2338" data-mp="24votgenhh2x6">
                  <dt className="text-gray-500" data-id="element-2339" data-mp="1zwvcgc8zmts9">Credits</dt>
                  <dd className="font-semibold text-gray-900 text-right" data-id="element-2340" data-mp="1zu93q9ad288g">
                    {program.credits}
                  </dd>
                </div>
                <div className="flex justify-between gap-4" data-id="element-2341" data-mp="2fa8f8m9h4z03">
                  <dt className="text-gray-500" data-id="element-2342" data-mp="2b96pqx5d01bb">Hours</dt>
                  <dd className="font-semibold text-gray-900 text-right" data-id="element-2343" data-mp="2b9ojnn4ju3g2">
                    {program.instructionalHours.toLocaleString()}
                  </dd>
                </div>
                <div className="flex justify-between gap-4" data-id="element-2344" data-mp="32zb9ctlaokbh">
                  <dt className="text-gray-500" data-id="element-2345" data-mp="2yyhipsggwrew">Program</dt>
                  <dd className="font-semibold text-gray-900 text-right" data-id="element-2346" data-mp="2ywwo62u5l8h1">
                    {domain?.name}
                  </dd>
                </div>
              </dl>
              <button type="button" onClick={scrollToForm} className="mt-6 w-full bg-cta hover:bg-cta-dark text-white font-bold py-3 px-5 rounded-lg text-sm uppercase tracking-wide transition-colors duration-200" data-id="element-2347" data-mp="2p01p9ityx628">
                Get Matched Free
              </button>
            </div>

            {related.length > 0 && <div className="border border-gray-200 rounded-2xl p-6" data-id="element-2348" data-mp="13jatvkqr9pbx">
                <h2 className="text-sm font-bold uppercase tracking-wider text-gray-500 mb-4" data-id="element-2349" data-mp="18g13pqbyvoow">
                  Also in This Domain
                </h2>
                <ul className="space-y-3" data-id="element-2350" data-mp="1de2pij0s2242">
                  {related.map(p => <li key={p.slug} data-id="element-2351" data-mp="1i7fc6nlwbyu6">
                      <Link to={`/program-information/${p.slug}`} className="group flex items-start justify-between gap-3" data-id="element-2352" data-mp="1i8gr536195h6">
                        <span className="text-sm text-gray-700 group-hover:text-navy transition-colors duration-200" data-id="element-2353" data-mp="1r6snlltkyxhj">
                          {p.name}
                          <span className="block text-xs text-gray-500" data-id="element-2354" data-mp="1w76j64dzweyo">
                            {p.credential}
                          </span>
                        </span>
                        <ArrowRightIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400 group-hover:text-navy transition-colors duration-200" aria-hidden="true" data-id="element-2355" data-mp="1tp4l6vkisfs" />
                      </Link>
                    </li>)}
                </ul>
              </div>}

            <div className="border border-gray-200 rounded-2xl p-6" data-id="element-2356" data-mp="3pxc4jedor0mp">
              <UserIcon className="w-6 h-6 mb-3 text-crimson" aria-hidden="true" data-id="element-2357" data-mp="3pz6l6ghxc6kz" />
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-2358" data-mp="8q9x29zmpch7">
                Not sure which program fits? Compare all six program domains and
                find the pathway that matches your goals.
              </p>
              <Link to={`/program-information/${programsIndexSlug}`} className="mt-4 inline-flex items-center text-sm font-semibold text-crimson hover:underline" data-id="element-2359" data-mp="hnakoeevj6jl">
                Browse all programs
                <ArrowRightIcon className="w-4 h-4 ml-1.5" aria-hidden="true" data-id="element-2360" data-mp="mgddgkbtubyq" />
              </Link>
            </div>
          </aside>
        </div>
      </div>

      {/* Careers. Full width, because the role cards need the room — a bare
          list of job titles tells a career changer nothing about whether the
          role suits them. */}
      <section aria-labelledby="careers-heading" className="border-t border-gray-200 bg-light" data-id="element-2361" data-mp="1f3wmx98ryy5w">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-id="element-2362" data-mp="1k0vg9pkm7131">
          <div className="max-w-3xl" data-id="element-2363" data-mp="162f6fprggugo">
            <div className="flex items-baseline justify-between gap-4 mb-4" data-id="element-2364" data-mp="161dl8sgq6rl8">
              <h2 id="careers-heading" className="text-2xl font-bold text-navy" data-id="element-2365" data-mp="1b0gqoe0maztq">
                Careers to Explore
              </h2>
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 whitespace-nowrap" data-id="element-2366" data-mp="wb1294ok76h7">
                {program.careerRoles.length} roles
              </span>
            </div>
            <p className="text-gray-700 leading-relaxed mb-8" data-id="element-2367" data-mp="ndicjmzz03yz">
              {program.careersIntro}
            </p>
          </div>

          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2368" data-mp="ddhoxov2j1oz">
            {program.careerRoles.map(role => {
            const detail = getCareerRoleDetail(role);
            return <li key={role} className="flex" data-id="element-2369" data-mp="ienj7r4dmx0m">
                  <div className="w-full bg-white border border-gray-200 rounded-xl p-5 flex flex-col hover:border-navy transition-colors duration-200" data-id="element-2370" data-mp="1kq8pz19jgn64">
                    <div className="flex items-start gap-3" data-id="element-2371" data-mp="1gqri616n3dxs">
                      <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-light flex items-center justify-center text-navy" aria-hidden="true" data-id="element-2372" data-mp="1bwdtsiqea72m">
                        <BriefcaseIcon className="w-4 h-4" data-id="element-2373" data-mp="11zhjq8kulcvy" />
                      </span>
                      <h3 className="text-sm font-bold text-navy leading-snug pt-1.5" data-id="element-2374" data-mp="11x4st5x43s6r">
                        {role}
                      </h3>
                    </div>
                    {detail && <>
                        <p className="mt-3 text-sm text-gray-600 leading-relaxed" data-id="element-2375" data-mp="nzpypnvwyw8g">
                          {detail.summary}
                        </p>
                        <p className="mt-auto pt-4 flex items-center gap-1.5 text-xs text-gray-500" data-id="element-2376" data-mp="2nbj1k15duvt3">
                          <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0 text-crimson" aria-hidden="true" data-id="element-2377" data-mp="2if294ur5hva7" />
                          {detail.setting}
                        </p>
                      </>}
                  </div>
                </li>;
          })}
          </ul>

          <p className="mt-6 max-w-3xl text-xs text-gray-500 leading-relaxed" data-id="element-2378" data-mp="2zmtgyykgrozq">
            Roles listed are examples of where graduates commonly begin. Actual
            hiring, pay, and advancement vary by employer, location, and
            experience — check current openings and Bureau of Labor Statistics
            data for your area.
          </p>

          {program.marketRelevance && <div className="mt-10 pt-10 border-t border-gray-200 max-w-3xl" data-id="element-2379" data-mp="2gwtzoxuv7e6v">
              <h2 className="text-xl font-bold text-navy mb-3 flex items-center gap-2" data-id="element-2380" data-mp="2gyy9fwfg37f2">
                <TrendingUpIcon className="w-5 h-5 text-crimson" aria-hidden="true" data-id="element-2381" data-mp="2kw2pxsqp4txo" />
                Market Relevance
              </h2>
              <p className="text-gray-700 leading-relaxed" data-id="element-2382" data-mp="7ey8ika9l32">
                {program.marketRelevance}
              </p>
            </div>}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-navy py-14" data-id="element-2383" data-mp="1s9re2yb1tkdn">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2384" data-mp="1s8zjnqt6tsoy">
          <h2 className="text-3xl font-bold text-white mb-4" data-id="element-2385" data-mp="269immjisq8k9">
            Ready to Take the Next Step?
          </h2>
          <p className="text-white/80 leading-relaxed mb-8" data-id="element-2386" data-mp="21bh3xzpa8b9b">
            Get matched with accredited schools offering the {program.name}{' '}
            {program.credential.toLowerCase()} program. Free, and no obligation.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-2387" data-mp="19h6r1xi169hr">
            <button type="button" onClick={scrollToForm} className="inline-flex items-center gap-2 bg-cta hover:bg-cta-dark text-white font-bold py-3.5 px-10 rounded-lg text-sm tracking-wide uppercase transition-colors duration-200" data-id="element-2388" data-mp="1nchj734w4gif">
              Get Matched Free
              <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-2389" data-mp="pu91hjy7557s" />
            </button>
            
          </div>
        </div>
      </section>

      <LegalFooter data-id="element-2390" data-mp="3692n2gn9nlgz" />
    </main>;
}